<template>
  <div
    v-show="visible"
    class="hls-player fixed z-[3000] w-[380px] overflow-hidden rounded-md border border-white/15 bg-[#13191d] shadow-2xl shadow-black/60"
    :style="{ left: `${position.left}px`, top: `${position.top}px` }"
  >
    <div
      class="flex cursor-move items-center gap-2 border-b border-white/10 bg-slate-800/80 px-2 py-1"
      @mousedown.prevent="startDrag"
    >
      <span class="min-w-0 flex-1 truncate text-xs font-semibold text-slate-200">{{ name || 'Playback' }}</span>

      <button
        v-if="pipSupported"
        class="h-6 w-6 shrink-0 rounded text-slate-400 transition-colors hover:bg-slate-700 hover:text-white"
        title="Pop out (picture in picture)"
        @mousedown.stop
        @click="togglePip"
      >
        <i class="el-icon-copy-document text-xs"></i>
      </button>
      <button
        class="h-6 w-6 shrink-0 rounded text-slate-400 transition-colors hover:bg-slate-700 hover:text-white"
        title="Close"
        @mousedown.stop
        @click="$emit('update:visible', false)"
      >
        <i class="el-icon-close text-xs"></i>
      </button>
    </div>

    <video ref="video" class="block w-full bg-black" controls autoplay playsinline muted></video>

    <div v-if="error" class="px-2 py-1 text-xs text-red-400">{{ error }}</div>
    <div class="truncate px-2 py-1 text-[10px] text-slate-500" :title="url">{{ url }}</div>
  </div>
</template>

<script>
import Hls from 'hls.js';

const WIDTH = 380;
const MARGIN = 16;
// Header + video (16:9) + url line, enough to keep the panel fully on screen while dragging.
const HEIGHT = 290;

export default {
  name: 'HlsPlayer',

  props: {
    visible: { type: Boolean, default: false },
    url: { type: String, default: '' },
    name: { type: String, default: '' },
  },

  data() {
    return {
      error: '',
      instance: null,
      position: { left: 0, top: 0 },
      // Set once the user moves it, so reopening keeps where they put it.
      positioned: false,
      drag: null,
    };
  },

  computed: {
    pipSupported() {
      return typeof document !== 'undefined' && document.pictureInPictureEnabled;
    },
  },

  watch: {
    visible(value) {
      if (value) this.start();
      else this.stop();
    },

    url(value) {
      if (this.visible && value) this.start();
    },
  },

  beforeDestroy() {
    this.stop();
    this.endDrag();
  },

  methods: {
    start() {
      this.stopPlayback();
      this.error = '';
      if (!this.positioned) this.resetPosition();

      this.$nextTick(() => {
        const video = this.$refs.video;
        if (!video || !this.url) return;
        // Safari plays HLS natively, everything else needs hls.js
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          video.src = this.url;
          return;
        }
        if (!Hls.isSupported()) {
          this.error = 'HLS playback is not supported in this browser.';
          return;
        }
        const hls = new Hls();
        hls.loadSource(this.url);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (!data.fatal) return;
          this.error = `Playback error: ${data.details}. The stream is only available while the event is running.`;
        });
        this.instance = hls;
      });
    },

    stop() {
      this.stopPlayback();
      this.error = '';
    },

    stopPlayback() {
      if (document.pictureInPictureElement === this.$refs.video) {
        document.exitPictureInPicture().catch(() => {});
      }
      if (this.instance) {
        this.instance.destroy();
        this.instance = null;
      }
      const video = this.$refs.video;
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    },

    async togglePip() {
      const video = this.$refs.video;
      if (!video) return;
      try {
        if (document.pictureInPictureElement) await document.exitPictureInPicture();
        else await video.requestPictureInPicture();
      } catch (error) {
        this.error = 'Picture in picture is unavailable for this stream.';
      }
    },

    resetPosition() {
      this.position = {
        left: Math.max(MARGIN, window.innerWidth - WIDTH - MARGIN),
        top: Math.max(MARGIN, window.innerHeight - HEIGHT - MARGIN),
      };
    },

    startDrag(event) {
      this.drag = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        left: this.position.left,
        top: this.position.top,
      };
      window.addEventListener('mousemove', this.onDrag);
      window.addEventListener('mouseup', this.endDrag);
    },

    onDrag(event) {
      if (!this.drag) return;
      const clamp = (value, max) => Math.min(Math.max(value, 0), Math.max(0, max));
      this.position = {
        left: clamp(this.drag.left + (event.clientX - this.drag.pointerX), window.innerWidth - WIDTH),
        top: clamp(this.drag.top + (event.clientY - this.drag.pointerY), window.innerHeight - 40),
      };
      this.positioned = true;
    },

    endDrag() {
      this.drag = null;
      window.removeEventListener('mousemove', this.onDrag);
      window.removeEventListener('mouseup', this.endDrag);
    },
  },
};
</script>
